import { ForwardRefComponent, HTMLMotionProps, motion, useAnimation, useInView, Variants } from 'framer-motion';
import { PropsWithChildren, useEffect, useRef } from 'react';
import styled, { DefaultTheme, StyledComponent } from 'styled-components';
import useBreakpoints from '../hooks/useBreakpoints';

export default function SystemSection() {
  const media = useBreakpoints({ defaultMedia: 'desktop' });
  const cardGridRef = useRef<HTMLDivElement>(null);
  const cardInView = useInView(cardGridRef);
  const controls = useAnimation();

  const variants: Variants = {
    initial: { y: 48, opacity: 0, transition: { delay: 0, duration: 0.2 } },
    animate: (delay) => ({ y: 0, opacity: 1, transition: { delay, duration: 0.5, ease: 'easeOut' } }),
  };

  useEffect(() => {
    if (cardInView) controls.start('animate');
    else controls.start('initial');
  }, [cardInView]);

  return (
    <Container>
      <Wrapper>
        <Bullet>청연만의 시스템</Bullet>
        <TitleWrapper>
          <FakeTitle>우리집 청소는 청소연구소</FakeTitle>
          <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
          <TitleTablet>{TitleTabletSvg}</TitleTablet>
          <TitleMobile>{TitleMobileSvg}</TitleMobile>
        </TitleWrapper>
        <CardGrid ref={cardGridRef} initial="initial" animate={controls}>
          <WomanCard variants={variants} custom={media === 'mobile' ? 0.1 : 0}>
            <picture>
              <source
                type="image/webp"
                srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home_clean/webp/woman.webp"
              />
              <source
                type="image/png"
                srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home_clean/woman.png"
              />
              <WomanImage src="https://cleanhome-web.cleaninglab.co.kr/brandsite/home_clean/woman.png" />
            </picture>
          </WomanCard>
          <SmartMatchingCard variants={variants} custom={media === 'mobile' ? 0 : 0.1}>
            <CardTitle>똑똑한 스마트매칭</CardTitle>
            <CardDescription>
              청소를 신청한 날짜와 집정보를 기반으로 적합한 매니저에게 업무를 추천하고 매칭이 진행됩니다.
            </CardDescription>
          </SmartMatchingCard>
          <CsCard variants={variants} custom={0.2}>
            <CardTitle>리뷰로 발전하는 우리집 청소</CardTitle>
            <CardDescription>
              남겨주신 소중한 리뷰로 서비스를 개선하고 품질 향상을 위해 노력합니다.
            </CardDescription>
          </CsCard>
          <ReviewCard variants={variants} custom={0.3}>
            <CardTitle>365 고객센터</CardTitle>
            <CardDescription>깨끗한 집에서 보내는 행복한 시간을 위해 365일 고객센터를 운영합니다.</CardDescription>
          </ReviewCard>
          <PayCard variants={variants} custom={0.4}>
            <CardTitle>안전하고 간편한 결제</CardTitle>
            <CardDescription>결제 수단을 한번만 등록하면 서비스 전날에 안전하게 결제 됩니다.</CardDescription>
            <picture style={{ position: 'absolute', inset: 0 }}>
              <source
                type="image/webp"
                srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home_clean/webp/payment.webp"
              />
              <source
                type="image/png"
                srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home_clean/payment.png"
              />
              <PayPhoneImage src="https://cleanhome-web.cleaninglab.co.kr/brandsite/home_clean/payment.png" />
            </picture>
          </PayCard>
          <InsuranceCard variants={variants} custom={0.5}>
            <CardTitle>파손보험 가입으로 안심</CardTitle>
            <CardDescription>파손보험에 가입되어 있어 안심하고 이용할 수 있습니다.</CardDescription>
          </InsuranceCard>
        </CardGrid>
      </Wrapper>
    </Container>
  );
}

const Container = styled.section`
  width: 100%;
  padding: 0 24px;
  padding-top: 130px;
  padding-bottom: 190px;
  position: relative;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 80px;
    padding-bottom: 100px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding-top: 80px;
    padding-bottom: 80px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 90px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-bottom: 60px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 75px;
  }
`;

const FakeTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: none;
  }
`;

const TitleMobile = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: block;
  }
`;

const Bullet = styled.h2`
  display: block;
  width: fit-content;
  font-weight: 600;
  font-size: 21px;
  line-height: 140%;
  color: #00bed6;
  padding: 12px 26px;
  border: 2px solid #00bed6;
  border-radius: 70px;
  margin: 0 auto;
  margin-bottom: 30px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 8px 18px;
    border: 1.5px solid #00bed6;
    border-radius: 50px;
    margin-bottom: 24px;
    font-size: 16px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 8px 16px;
    margin-bottom: 24px;
    font-size: 14px;
  }
`;

const CardGrid = styled(motion.div)`
  display: grid;
  width: fit-content;
  margin: 0 auto;
  grid-template:
    'woman smartmatching smartmatching' 140px
    'woman cs review' 250px
    'pay pay insurance' 250px
    / 208px 284px 284px;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    grid-template:
      'woman smartmatching smartmatching' 126px
      'woman cs review' 217px
      'pay pay insurance' 194px
      / 163px 270px 239px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    grid-template:
      'smartmatching smartmatching' auto
      'woman cs' auto
      'review review' auto
      'pay pay' auto
      'insurance insurance' auto
      / 1fr 1fr;
  }
`;

const Card = styled(motion.article)`
  position: relative;
  border-radius: 16px;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    border-radius: 8px;
  }
`;

const CardTitle = styled.h1`
  font-weight: 600;
  font-size: 30px;
  line-height: 125%;
  word-break: keep-all;
  color: rgba(0, 0, 0, 0.85);

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 27px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-size: 21px;
  }
`;

const CardDescription = styled.h2`
  font-weight: 300;
  font-size: 18px;
  line-height: 140%;
  color: rgba(0, 0, 0, 0.6);

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 16px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-size: 16px;
    font-weight: 400;
  }
`;

const WomanCard = styled(Card)`
  grid-area: woman;
  background: #eeeeee;
  position: relative;
`;

const SmartMatchingCard = styled(Card)`
  grid-area: smartmatching;
  background: #00bdd6;

  padding: 24px;
  padding-right: 60px;
  display: flex;
  flex-direction: row;

  ${CardTitle} {
    margin-right: 70px;
    color: #ffffff;
  }

  ${CardDescription} {
    color: #e4f8fb;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 24px;

    ${CardTitle} {
      margin-right: 56px;
    }
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    flex-direction: column;
    background: #004a80;

    ${CardTitle} {
      margin-right: 0;
      margin-bottom: 21px;
    }
  }
`;

const CsCard = styled(Card)`
  grid-area: cs;
  background: #e9f7f8;
  padding: 24px 40px 27px 24px;
  padding-bottom: 27px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${CardTitle} {
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 24px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    background: #00bdd6;

    ${CardTitle} {
      margin-bottom: 53px;
      color: #ffffff;
    }

    ${CardDescription} {
      color: #ffffff;
    }
  }
`;

const ReviewCard = styled(Card)`
  grid-area: review;
  background: #f5f5f7;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px 36px 27px 24px;

  ${CardTitle} {
    width: 70%;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 24px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    background: #e9f7f8;

    ${CardTitle} {
      margin-bottom: 20px;
    }
  }
`;

const PayCard = styled(Card)`
  position: relative;
  grid-area: pay;
  background: #f5f5f7;
  padding: 24px 24px 27px 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${CardTitle} {
    width: 40%;
  }

  ${CardDescription} {
    width: 50%;
    word-break: keep-all;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 24px;

    ${CardDescription} {
      width: 60%;
    }
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    background: #004a80;

    ${CardTitle} {
      color: #ffffff;
      margin-bottom: 20px;
    }

    ${CardDescription} {
      color: #ffffff;
      width: 50%;
      word-break: break-all;
    }
  }
`;

const InsuranceCard = styled(Card)`
  grid-area: insurance;
  background: #004a80;
  padding: 24px 36px 27px 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${CardTitle} {
    width: 90%;
    color: #ffffff;
  }

  ${CardDescription} {
    color: #ccdbe6;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 24px;

    ${CardTitle} {
      width: 100%;
    }
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    background: #f5f5f7;

    ${CardTitle} {
      color: rgba(0, 0, 0, 0.85);
      margin-bottom: 20px;
    }

    ${CardDescription} {
      color: rgba(0, 0, 0, 0.6);
    }
  }
`;

const WomanImage = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 40%;
`;

const PayPhoneImage = styled.img`
  position: absolute;
  right: 40px;
  bottom: 0;
  height: calc(100% - 40px);
  object-fit: cover;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    right: 30px;
    height: calc(100% - 30px);
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    right: 28px;
    height: calc(100% - 28px);
  }
`;

const TitleDesktopSvg = (
  <svg width="478" height="45" viewBox="0 0 478 45" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M27.3152 11.516C27.3152 14.92 24.2332 17.634 20.0472 17.634C15.7232 17.634 12.7792 14.92 12.7792 11.516C12.7792 8.02 15.7232 5.536 20.0472 5.536C24.2332 5.536 27.3152 8.02 27.3152 11.516ZM22.5772 42.474V28.674H39.4132V24.35H0.681207V28.674H17.5632V42.474H22.5772ZM32.5592 11.516C32.5592 5.076 27.0392 0.981998 20.0472 0.981998C13.2392 0.981998 7.53521 5.03 7.53521 11.516C7.53521 18.324 13.5612 22.004 20.0472 22.004C26.4412 22.004 32.5592 18.324 32.5592 11.516ZM68.9416 23.43C63.6516 24.534 54.7276 24.994 49.8516 24.764V17.726H64.1576V2.868H44.6536V7.284H59.0516V13.402H44.7456V29.042C52.3816 29.41 63.3296 29.134 69.4936 27.708L68.9416 23.43ZM72.8056 39.07H77.8656V0.751998H72.8056V39.07ZM114.662 39.852H99.942V34.976H114.662V39.852ZM114.662 23.66H119.768V0.659999H114.662V23.66ZM94.606 6.916C94.56 9.998 93.686 12.62 91.892 14.736C90.098 16.898 87.66 18.508 84.578 19.612L86.97 23.798C91.57 21.958 95.664 18.646 97.32 14.552C99.344 18.554 103.392 21.636 107.854 23.108L110.338 18.968C107.164 17.956 104.634 16.438 102.748 14.414C100.954 12.436 99.988 9.906 99.942 6.916H108.268V2.546H85.912V6.916H94.606ZM119.768 44.222V25.96H114.662V30.698H99.942V25.96H94.836V44.222H119.768ZM156.347 5.214V0.843999H143.191V5.214H156.347ZM167.433 35.804C167.433 38.748 164.857 40.588 161.039 40.588C157.359 40.588 154.737 38.748 154.737 35.804C154.737 32.86 157.359 31.02 161.039 31.02C164.857 31.02 167.433 32.86 167.433 35.804ZM173.137 26.144V0.614H168.031V13.862H160.763V18.14H168.031V26.144H173.137ZM147.009 12.206C146.917 13.31 146.687 14.276 146.273 15.15C144.709 18.692 141.167 20.808 137.211 22.326L139.603 26.374C144.157 25.04 148.481 21.36 149.907 18.048C151.379 21.314 156.025 24.304 160.073 25.546L162.281 21.406C158.187 20.302 154.783 18.14 153.403 14.874C153.081 14.092 152.851 13.172 152.759 12.206H161.085V7.882H138.407V12.206H147.009ZM172.585 35.804C172.585 30.514 167.801 26.696 161.039 26.696C154.415 26.696 149.585 30.514 149.585 35.804C149.585 41.186 154.415 44.958 161.039 44.958C167.801 44.958 172.585 41.186 172.585 35.804ZM216.971 34.976V30.56H200.135V21.636H195.121V30.56H178.239V34.976H216.971ZM194.661 1.81V4.34C194.661 11.7 188.359 16.944 181.091 18.784L183.483 23.2C189.923 21.084 195.443 17.266 197.605 12.252C199.997 17.22 205.011 21.13 211.773 23.154L214.119 18.738C206.943 16.76 200.549 11.746 200.549 4.34V1.81H194.661ZM258.368 25.546V21.222H219.636V25.546H258.368ZM226.168 1.12V16.622H252.48V12.206H231.32V1.12H226.168ZM225.938 29.134V43.026H252.434V38.702H230.998V29.134H225.938ZM292.509 5.214V0.843999H279.353V5.214H292.509ZM303.595 35.804C303.595 38.748 301.019 40.588 297.201 40.588C293.521 40.588 290.899 38.748 290.899 35.804C290.899 32.86 293.521 31.02 297.201 31.02C301.019 31.02 303.595 32.86 303.595 35.804ZM309.299 26.144V0.614H304.193V13.862H296.925V18.14H304.193V26.144H309.299ZM283.171 12.206C283.079 13.31 282.849 14.276 282.435 15.15C280.871 18.692 277.329 20.808 273.373 22.326L275.765 26.374C280.319 25.04 284.643 21.36 286.069 18.048C287.541 21.314 292.187 24.304 296.235 25.546L298.443 21.406C294.349 20.302 290.945 18.14 289.565 14.874C289.243 14.092 289.013 13.172 288.921 12.206H297.247V7.882H274.569V12.206H283.171ZM308.747 35.804C308.747 30.514 303.963 26.696 297.201 26.696C290.577 26.696 285.747 30.514 285.747 35.804C285.747 41.186 290.577 44.958 297.201 44.958C303.963 44.958 308.747 41.186 308.747 35.804ZM353.133 34.976V30.56H336.297V21.636H331.283V30.56H314.401V34.976H353.133ZM330.823 1.81V4.34C330.823 11.7 324.521 16.944 317.253 18.784L319.645 23.2C326.085 21.084 331.605 17.266 333.767 12.252C336.159 17.22 341.173 21.13 347.935 23.154L350.281 18.738C343.105 16.76 336.711 11.746 336.711 4.34V1.81H330.823ZM386.94 6.502H376.176C374.152 4.386 371.208 3.052 367.758 3.052C361.226 3.052 356.534 7.698 356.534 13.632C356.534 19.704 361.226 24.212 367.758 24.212C371.438 24.212 374.566 22.786 376.544 20.394H386.94V30.284H392.092V0.705998H386.94V6.502ZM392.69 42.934V38.518H371.898V27.8H366.792V42.934H392.69ZM373.922 13.586C373.922 17.082 371.254 19.796 367.758 19.796C364.17 19.796 361.594 17.082 361.594 13.586C361.594 10.09 364.17 7.514 367.758 7.514C371.254 7.514 373.922 10.09 373.922 13.586ZM378.982 13.632C378.982 12.758 378.844 11.838 378.66 11.01H386.94V15.978H378.752C378.89 15.242 378.982 14.46 378.982 13.632ZM419.182 42.014V25.592H435.926V21.13H428.244C428.474 20.256 428.658 19.244 428.796 18.186C429.532 13.586 430.038 7.928 429.762 2.27H402.898V6.732H424.656C424.748 9.86 424.38 14.414 423.736 18.37L423.23 21.13H397.194V25.592H414.076V42.014H419.182ZM477.322 34.976V30.56H460.486V21.636H455.472V30.56H438.59V34.976H477.322ZM455.012 1.81V4.34C455.012 11.7 448.71 16.944 441.442 18.784L443.834 23.2C450.274 21.084 455.794 17.266 457.956 12.252C460.348 17.22 465.362 21.13 472.124 23.154L474.47 18.738C467.294 16.76 460.9 11.746 460.9 4.34V1.81H455.012Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="287" height="27" viewBox="0 0 287 27" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.0102 7.042C16.0102 9.04 14.2012 10.633 11.7442 10.633C9.20622 10.633 7.47822 9.04 7.47822 7.042C7.47822 4.99 9.20622 3.532 11.7442 3.532C14.2012 3.532 16.0102 4.99 16.0102 7.042ZM13.2292 25.213V17.113H23.1112V14.575H0.377219V17.113H10.2862V25.213H13.2292ZM19.0882 7.042C19.0882 3.262 15.8482 0.858999 11.7442 0.858999C7.74822 0.858999 4.40022 3.235 4.40022 7.042C4.40022 11.038 7.93722 13.198 11.7442 13.198C15.4972 13.198 19.0882 11.038 19.0882 7.042ZM40.9831 14.035C37.8781 14.683 32.6401 14.953 29.7781 14.818V10.687H38.1751V1.966H26.7271V4.558H35.1781V8.149H26.7811V17.329C31.2631 17.545 37.6891 17.383 41.3071 16.546L40.9831 14.035ZM43.2511 23.215H46.2211V0.723999H43.2511V23.215ZM68.359 23.674H59.719V20.812H68.359V23.674ZM68.359 14.17H71.356V0.669999H68.359V14.17ZM56.587 4.342C56.56 6.151 56.047 7.69 54.994 8.932C53.941 10.201 52.51 11.146 50.701 11.794L52.105 14.251C54.805 13.171 57.208 11.227 58.18 8.824C59.368 11.173 61.744 12.982 64.363 13.846L65.821 11.416C63.958 10.822 62.473 9.931 61.366 8.743C60.313 7.582 59.746 6.097 59.719 4.342H64.606V1.777H51.484V4.342H56.587ZM71.356 26.239V15.52H68.359V18.301H59.719V15.52H56.722V26.239H71.356ZM93.9063 3.343V0.778H86.1843V3.343H93.9063ZM100.413 21.298C100.413 23.026 98.9013 24.106 96.6603 24.106C94.5003 24.106 92.9613 23.026 92.9613 21.298C92.9613 19.57 94.5003 18.49 96.6603 18.49C98.9013 18.49 100.413 19.57 100.413 21.298ZM103.761 15.628V0.643H100.764V8.419H96.4983V10.93H100.764V15.628H103.761ZM88.4253 7.447C88.3713 8.095 88.2363 8.662 87.9933 9.175C87.0753 11.254 84.9963 12.496 82.6743 13.387L84.0783 15.763C86.7513 14.98 89.2893 12.82 90.1263 10.876C90.9903 12.793 93.7173 14.548 96.0933 15.277L97.3893 12.847C94.9863 12.199 92.9883 10.93 92.1783 9.013C91.9893 8.554 91.8543 8.014 91.8003 7.447H96.6873V4.909H83.3763V7.447H88.4253ZM103.437 21.298C103.437 18.193 100.629 15.952 96.6603 15.952C92.7723 15.952 89.9373 18.193 89.9373 21.298C89.9373 24.457 92.7723 26.671 96.6603 26.671C100.629 26.671 103.437 24.457 103.437 21.298ZM130.03 20.812V18.22H120.148V12.982H117.205V18.22H107.296V20.812H130.03ZM116.935 1.345V2.83C116.935 7.15 113.236 10.228 108.97 11.308L110.374 13.9C114.154 12.658 117.394 10.417 118.663 7.474C120.067 10.39 123.01 12.685 126.979 13.873L128.356 11.281C124.144 10.12 120.391 7.177 120.391 2.83V1.345H116.935ZM154.868 15.277V12.739H132.134V15.277H154.868ZM135.968 0.939999V10.039H151.412V7.447H138.992V0.939999H135.968ZM135.833 17.383V25.537H151.385V22.999H138.803V17.383H135.833ZM175.987 3.343V0.778H168.265V3.343H175.987ZM182.494 21.298C182.494 23.026 180.982 24.106 178.741 24.106C176.581 24.106 175.042 23.026 175.042 21.298C175.042 19.57 176.581 18.49 178.741 18.49C180.982 18.49 182.494 19.57 182.494 21.298ZM185.842 15.628V0.643H182.845V8.419H178.579V10.93H182.845V15.628H185.842ZM170.506 7.447C170.452 8.095 170.317 8.662 170.074 9.175C169.156 11.254 167.077 12.496 164.755 13.387L166.159 15.763C168.832 14.98 171.37 12.82 172.207 10.876C173.071 12.793 175.798 14.548 178.174 15.277L179.47 12.847C177.067 12.199 175.069 10.93 174.259 9.013C174.07 8.554 173.935 8.014 173.881 7.447H178.768V4.909H165.457V7.447H170.506ZM185.518 21.298C185.518 18.193 182.71 15.952 178.741 15.952C174.853 15.952 172.018 18.193 172.018 21.298C172.018 24.457 174.853 26.671 178.741 26.671C182.71 26.671 185.518 24.457 185.518 21.298ZM212.111 20.812V18.22H202.229V12.982H199.286V18.22H189.377V20.812H212.111ZM199.016 1.345V2.83C199.016 7.15 195.317 10.228 191.051 11.308L192.455 13.9C196.235 12.658 199.475 10.417 200.744 7.474C202.148 10.39 205.091 12.685 209.06 13.873L210.437 11.281C206.225 10.12 202.472 7.177 202.472 2.83V1.345H199.016ZM232.494 4.099H226.176C224.988 2.857 223.26 2.074 221.235 2.074C217.401 2.074 214.647 4.801 214.647 8.284C214.647 11.848 217.401 14.494 221.235 14.494C223.395 14.494 225.231 13.657 226.392 12.253H232.494V18.058H235.518V0.696999H232.494V4.099ZM235.869 25.483V22.891H223.665V16.6H220.668V25.483H235.869ZM224.853 8.257C224.853 10.309 223.287 11.902 221.235 11.902C219.129 11.902 217.617 10.309 217.617 8.257C217.617 6.205 219.129 4.693 221.235 4.693C223.287 4.693 224.853 6.205 224.853 8.257ZM227.823 8.284C227.823 7.771 227.742 7.231 227.634 6.745H232.494V9.661H227.688C227.769 9.229 227.823 8.77 227.823 8.284ZM251.959 24.943V15.304H261.787V12.685H257.278C257.413 12.172 257.521 11.578 257.602 10.957C258.034 8.257 258.331 4.936 258.169 1.615H242.401V4.234H255.172C255.226 6.07 255.01 8.743 254.632 11.065L254.335 12.685H239.053V15.304H248.962V24.943H251.959ZM286.625 20.812V18.22H276.743V12.982H273.8V18.22H263.891V20.812H286.625ZM273.53 1.345V2.83C273.53 7.15 269.831 10.228 265.565 11.308L266.969 13.9C270.749 12.658 273.989 10.417 275.258 7.474C276.662 10.39 279.605 12.685 283.574 13.873L284.951 11.281C280.739 10.12 276.986 7.177 276.986 2.83V1.345H273.53Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleMobileSvg = (
  <svg width="282" height="27" viewBox="0 0 282 27" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.1862 7.123C16.1862 9.04 14.5662 10.39 12.2442 10.39C9.86822 10.39 8.30222 9.04 8.30222 7.123C8.30222 5.26 9.89522 3.856 12.2442 3.856C14.5662 3.856 16.1862 5.26 16.1862 7.123ZM13.9182 25.078V17.167H23.5032V14.386H0.985219V17.167H10.5972V25.078H13.9182ZM19.5612 7.123C19.5612 3.289 16.3212 0.939999 12.2442 0.939999C8.30222 0.939999 4.92722 3.235 4.92722 7.123C4.92722 11.308 8.49122 13.279 12.2442 13.279C15.9702 13.279 19.5612 11.281 19.5612 7.123ZM40.7941 13.9C37.8241 14.494 32.8291 14.764 30.1561 14.629V10.822H38.2291V2.047H26.8081V4.855H34.9081V8.095H26.8621V17.356C31.2361 17.572 37.6891 17.356 41.1451 16.627L40.7941 13.9ZM42.9001 23.107H46.1941V0.831999H42.9001V23.107ZM67.535 23.377H59.489V20.785H67.535V23.377ZM67.535 14.17H70.802V0.804999H67.535V14.17ZM64.106 4.612V1.831H51.011V4.612H55.952C55.79 7.906 53.657 10.444 50.228 11.632L51.74 14.305C54.278 13.333 56.762 11.362 57.707 9.04C58.733 11.335 61.406 13.117 63.755 13.9L65.321 11.281C61.676 10.201 59.543 7.879 59.381 4.612H64.106ZM70.802 26.131V15.412H67.535V18.031H59.489V15.412H56.222V26.131H70.802ZM92.5683 3.586V0.804999H84.7923V3.586H92.5683ZM98.7243 21.19C98.7243 22.783 97.2393 23.782 95.2683 23.782C93.2433 23.782 91.8393 22.783 91.8393 21.19C91.8393 19.624 93.2433 18.625 95.2683 18.625C97.2393 18.625 98.7243 19.624 98.7243 21.19ZM102.207 15.79V0.778H98.9133V8.365H95.0523V11.065H98.9133V15.79H102.207ZM95.2683 7.69V4.909H82.0653V7.69H86.8713C86.4663 10.579 84.0093 12.361 81.3633 13.171L82.8483 15.79C85.4943 14.98 87.9513 12.901 88.7613 11.065C89.5983 12.874 92.3523 14.737 94.6203 15.277L96.0243 12.604C93.2163 11.902 91.0023 10.444 90.6243 7.69H95.2683ZM101.991 21.19C101.991 18.085 99.1563 15.871 95.2683 15.871C91.3803 15.871 88.5453 18.085 88.5453 21.19C88.5453 24.403 91.3803 26.536 95.2683 26.536C99.1563 26.536 101.991 24.403 101.991 21.19ZM127.922 20.812V18.058H118.337V12.955H115.016V18.058H105.404V20.812H127.922ZM114.746 1.426V2.911C114.746 7.285 111.047 10.039 107.051 11.119L108.536 13.954C112.235 12.82 115.286 10.633 116.663 7.69C118.121 10.525 121.01 12.712 124.817 13.9L126.302 11.092C122.36 9.958 118.58 7.285 118.58 2.911V1.426H114.746ZM152.26 15.304V12.496H129.742V15.304H152.26ZM133.495 1.048V10.174H148.858V7.393H136.816V1.048H133.495ZM133.36 17.167V25.429H148.831V22.648H136.681V17.167H133.36ZM172.649 3.586V0.804999H164.873V3.586H172.649ZM178.805 21.19C178.805 22.783 177.32 23.782 175.349 23.782C173.324 23.782 171.92 22.783 171.92 21.19C171.92 19.624 173.324 18.625 175.349 18.625C177.32 18.625 178.805 19.624 178.805 21.19ZM182.288 15.79V0.778H178.994V8.365H175.133V11.065H178.994V15.79H182.288ZM175.349 7.69V4.909H162.146V7.69H166.952C166.547 10.579 164.09 12.361 161.444 13.171L162.929 15.79C165.575 14.98 168.032 12.901 168.842 11.065C169.679 12.874 172.433 14.737 174.701 15.277L176.105 12.604C173.297 11.902 171.083 10.444 170.705 7.69H175.349ZM182.072 21.19C182.072 18.085 179.237 15.871 175.349 15.871C171.461 15.871 168.626 18.085 168.626 21.19C168.626 24.403 171.461 26.536 175.349 26.536C179.237 26.536 182.072 24.403 182.072 21.19ZM208.003 20.812V18.058H198.418V12.955H195.097V18.058H185.485V20.812H208.003ZM194.827 1.426V2.911C194.827 7.285 191.128 10.039 187.132 11.119L188.617 13.954C192.316 12.82 195.367 10.633 196.744 7.69C198.202 10.525 201.091 12.712 204.898 13.9L206.383 11.092C202.441 9.958 198.661 7.285 198.661 2.911V1.426H194.827ZM210.201 8.365C210.201 11.875 212.928 14.548 216.816 14.548C218.976 14.548 220.785 13.711 221.973 12.334H227.697V18.004H230.964V0.831999H227.697V4.045H221.676C220.488 2.857 218.787 2.128 216.816 2.128C212.928 2.128 210.201 4.882 210.201 8.365ZM231.315 25.375V22.54H219.489V16.519H216.195V25.375H231.315ZM213.495 8.338C213.495 6.394 214.899 4.99 216.816 4.99C218.679 4.99 220.11 6.394 220.11 8.338C220.11 10.282 218.679 11.74 216.816 11.74C214.899 11.74 213.495 10.282 213.495 8.338ZM223.323 9.499C223.404 9.148 223.431 8.77 223.431 8.365C223.431 7.879 223.377 7.393 223.269 6.907H227.697V9.499H223.323ZM247.121 24.835V15.439H256.679V12.577H252.305C252.926 9.661 253.277 5.665 253.169 1.669H237.455V4.504H249.848C249.956 6.61 249.686 10.255 249.146 12.577H234.161V15.439H243.773V24.835H247.121ZM281.017 20.812V18.058H271.432V12.955H268.111V18.058H258.499V20.812H281.017ZM267.841 1.426V2.911C267.841 7.285 264.142 10.039 260.146 11.119L261.631 13.954C265.33 12.82 268.381 10.633 269.758 7.69C271.216 10.525 274.105 12.712 277.912 13.9L279.397 11.092C275.455 9.958 271.675 7.285 271.675 2.911V1.426H267.841Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
